import React, {FC} from 'react';
import styled, {css} from 'styled-components';
import {
  Countdown,
  CountdownContainerProps,
  CountdownSize,
  CountdownUnitProps,
} from './Countdown';
import {useTimeUnitTranslation} from '@aboutyou/i18n';
import {capitalize} from '@aboutyou/utils';
import {GenericIconName} from '../../atoms/Icon/IconName';
import {Icon} from '../../atoms/Icon/Icon';
import {Text} from '../../atoms/Text/Text';
import {COLORS} from '../../../constants/colors';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';

interface Props {
  endDate: Date;
  className?: string;
  light?: boolean;
  size?: CountdownSize;
  withBorder?: boolean;
  withIcon?: boolean;
  backgroundColor?: string;
  fontColor?: string;
  onTimeout?: () => void;
}

function getFontColor(fontColor?: string, backgroundColor?: string) {
  if (fontColor) {
    return fontColor;
  }
  if (!fontColor && backgroundColor) {
    return COLORS.White;
  }

  return COLORS.Black;
}

export const CountdownBoxed: FC<Props> = ({
  endDate,
  className,
  light,
  size,
  withBorder,
  withIcon = true,
  fontColor,
  backgroundColor,
  onTimeout,
}) => {
  return (
    <Wrapper
      data-testid="CountdownBox"
      $fontColor={getFontColor(fontColor, backgroundColor)}
      $backgroundColor={backgroundColor}
      $size={size}
      $showBorder={size === 'small' && withBorder}
      className={className}
      $withShadow={size === 'normal'}
    >
      {withIcon && (
        <StyledIcon
          size={!light ? 'S' : undefined}
          width={light ? '20px' : undefined}
          height={light ? '20px' : undefined}
          asset={GenericIconName.Clock}
          // if we add fill as style it has too large priority and we wont be able to override it with css selectors
          $fill={getFontColor(fontColor, backgroundColor)}
        />
      )}
      <Countdown
        endDate={endDate}
        unitComponent={(props: CountdownUnitProps) => (
          <TimeUnitSection {...props} light={light} />
        )}
        containerComponent={
          size === 'small' ? CountdownContainerReduced : CountdownContainer
        }
        size={size}
        onTimeout={onTimeout}
      />
    </Wrapper>
  );
};

const TimeUnitSection: FC<CountdownUnitProps> = ({
  value,
  unit,
  size,
  light,
}) => {
  const timeFormat = useTimeUnitTranslation('shorter');
  const testId = `countdown${capitalize(unit)}`;

  switch (unit) {
    case 'days':
      if (value === '00') {
        return null;
      }
      return (
        <TimeUnit
          size={size}
          testId={testId}
          unit={timeFormat[unit]}
          value={value}
          light={light}
        />
      );
    case 'hours':
    case 'minutes':
    case 'seconds':
      return (
        <TimeUnit
          size={size}
          testId={testId}
          unit={timeFormat[unit]}
          value={value}
          light={light}
        />
      );
    default:
      return null;
  }
};

const labelSizes: {[key in CountdownSize]: string} = {
  small: 'auto',
  normal: '27px',
};

const TimeUnit: FC<{
  testId: string;
  unit: string;
  value: string;
  light?: boolean;
  size?: CountdownSize;
}> = ({testId, unit, value, size, light}) => {
  const labelProps: {
    size: keyof typeof FONT_SIZES;
    weight: keyof typeof FONT_WEIGHTS;
  } = {
    size: 'px18',
    weight: 'bold',
  };

  if (size === 'small') {
    labelProps.size = 'px12';
    labelProps.weight = 'heavy';
  }
  if (light) {
    labelProps.size = 'px10';
    labelProps.weight = 'light';
  }
  return (
    <TimeUnitWrapper data-testid={testId} $light={light}>
      <StyledLabel {...labelProps} width={labelSizes[size || 'normal']}>
        {value}
      </StyledLabel>
      <StyledLabel {...labelProps}>{unit}</StyledLabel>
    </TimeUnitWrapper>
  );
};

const Wrapper = styled.div<{
  $fontColor: string;
  $size?: CountdownSize;
  $backgroundColor?: string;
  $showBorder?: boolean;
  $withShadow?: boolean;
}>`
  padding: 0 5px;
  display: inline-flex;
  align-items: center;
  border-radius: 3px;
  background-color: ${props =>
    props.$backgroundColor ? props.$backgroundColor : COLORS.White};
  color: ${props => props.$fontColor};
  ${props =>
    props.$withShadow &&
    css`
      box-shadow: 0 10px 15px 0 rgba(0, 0, 0, 0.08),
        0 4px 2px 0 rgba(0, 0, 0, 0.05);
    `}
  ${props =>
    props.$showBorder &&
    css`
      border: 1px solid white;
    `}
`;

const CountdownContainer = styled.div<CountdownContainerProps>`
  padding: 5px 5px 7px;
  display: flex;
`;

const CountdownContainerReduced = styled.div`
  padding: 5px 0 5px 4px;
  border-color: white;
  display: flex;
`;

const TimeUnitWrapper = styled.div<{$light?: boolean}>`
  color: inherit;
  display: flex;
  min-width: ${({$light}) => ($light ? '25px' : '32px')};
  justify-content: center;
`;

const StyledLabel = styled(Text)<{
  width?: string;
}>`
  color: inherit;
  letter-spacing: 1px;
  width: ${({width}) => width ?? 'auto'};
  text-align: ${({width}) => (width ? 'right' : 'left')};
  text-transform: uppercase;
`;

const StyledIcon = styled(Icon)<{$fill?: string}>`
  ${({$fill}) => css`
    fill: ${$fill};
  `}
`;
